<script setup>
import { computed, ref } from 'vue'
import { ChevronDown, Search } from '@lucide/vue'
import { Button, EmptyState, Input, Modal } from '@drama/ui'

const props = defineProps({
  modelValue: { type: String, required: true },
  items: { type: Array, required: true },
  allowEmpty: { type: Boolean, default: false },
  emptyLabel: { type: String, default: '' },
  searchPlaceholder: { type: String, required: true },
  emptyResult: { type: String, required: true },
})

const emit = defineEmits(['update:modelValue'])
const open = ref(false)
const query = ref('')

const selected = computed(() => props.items.find(item => item.scenario_id === props.modelValue))
const visibleItems = computed(() => {
  const keyword = query.value.trim().toLocaleLowerCase()
  if (keyword === '') return props.items
  return props.items.filter(item => [item.display_title, item.series_title, item.scenario_id]
    .filter(value => value !== null)
    .some(value => value.toLocaleLowerCase().includes(keyword)))
})

function showOptions() {
  query.value = ''
  open.value = true
}

function choose(scenarioId) {
  emit('update:modelValue', scenarioId)
  open.value = false
}
</script>

<template>
  <Button class="scenario-picker__trigger" variant="secondary" size="sm" @click="showOptions">
    <img v-if="selected?.preview_url" :src="selected.preview_url" :alt="selected.display_title">
    <span class="scenario-picker__trigger-copy">
      <strong>{{ selected?.display_title ?? emptyLabel }}</strong>
      <small v-if="selected?.series_title">{{ selected.series_title }}</small>
    </span>
    <ChevronDown :size="15" />
  </Button>

  <Modal :open="open" :title="emptyLabel" width="min(92vw, 920px)" @update:open="open = $event">
    <div class="scenario-picker__search">
      <Search :size="17" />
      <Input v-model="query" type="search" :placeholder="searchPlaceholder" />
    </div>

    <div class="scenario-picker__grid">
      <button
        v-if="allowEmpty"
        type="button"
        class="scenario-picker__option scenario-picker__option--all"
        :class="{ 'is-selected': modelValue === '' }"
        @click="choose('')"
      >
        <strong>{{ emptyLabel }}</strong>
      </button>
      <button
        v-for="item in visibleItems"
        :key="item.scenario_id"
        type="button"
        class="scenario-picker__option"
        :class="{ 'is-selected': item.scenario_id === modelValue }"
        @click="choose(item.scenario_id)"
      >
        <img v-if="item.preview_url" :src="item.preview_url" :alt="item.display_title">
        <div v-else class="scenario-picker__placeholder" />
        <span>
          <strong>{{ item.display_title }}</strong>
          <small v-if="item.series_title">{{ item.series_title }}</small>
          <code>{{ item.scenario_id }}</code>
        </span>
      </button>
    </div>
    <EmptyState v-if="visibleItems.length === 0" :title="emptyResult" />
  </Modal>
</template>

<style scoped>
.scenario-picker__trigger { max-width: min(100%, 420px); }
.scenario-picker__trigger img { width: 40px; height: 28px; border-radius: var(--drama-radius-xs); object-fit: cover; }
.scenario-picker__trigger-copy { display: grid; min-width: 0; text-align: left; }
.scenario-picker__trigger-copy strong,
.scenario-picker__trigger-copy small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.scenario-picker__trigger-copy small { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.scenario-picker__search { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: var(--drama-space-sm); margin-bottom: var(--drama-space-lg); color: var(--drama-color-text-muted); }
.scenario-picker__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: var(--drama-space-md); }
.scenario-picker__option { display: grid; grid-template-columns: 112px minmax(0, 1fr); gap: var(--drama-space-md); align-items: center; padding: var(--drama-space-sm); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface); color: var(--drama-color-text); cursor: pointer; text-align: left; }
.scenario-picker__option:hover,
.scenario-picker__option.is-selected { border-color: var(--drama-color-primary); background: var(--drama-color-surface-alt); }
.scenario-picker__option img,
.scenario-picker__placeholder { width: 112px; aspect-ratio: 16 / 9; border-radius: var(--drama-radius-sm); background: var(--drama-color-surface-alt); object-fit: cover; }
.scenario-picker__option span { display: grid; gap: var(--drama-space-xs); min-width: 0; }
.scenario-picker__option strong,
.scenario-picker__option small,
.scenario-picker__option code { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.scenario-picker__option small,
.scenario-picker__option code { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.scenario-picker__option--all { display: flex; min-height: 88px; justify-content: center; }
@media (max-width: 620px) {
  .scenario-picker__grid { grid-template-columns: 1fr; }
}
</style>
